// LegalUpdatesPage.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  lazy,
  Suspense,
  useCallback,
} from 'react';
import api from '../api';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ThemeProvider,
  createTheme,
  CssBaseline,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Pagination,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  useParams,
  useSearchParams,
  useNavigate,
  Link,
  useLocation,
} from 'react-router-dom';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MultiDatePicker } from '../Components/MultiDatePicker.js';
import { MultiStringInput } from '../Components/MultiStringInput.js';
import ViewListIcon from '@mui/icons-material/ViewList';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import BackupIcon from '@mui/icons-material/Backup';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import TodayIcon from '@mui/icons-material/Today';
import UpdateIcon from '@mui/icons-material/Update';
import 'react-quill/dist/quill.snow.css';
import LegalUpdateDetail from './LegalUpdateDetail';
import { useAuth } from '../context/userContext';
import Layout from '../Layout';
import 'suneditor/dist/css/suneditor.min.css';
import { toast } from 'react-toastify';
import SearchBar from '../Components/SearchBarLegalUpdate';
import { DEPARTMENT_OPTIONS, LEGAL_UPDATE_DEPARTMENT_OPTIONS } from '../constants/departments';

const SunEditor = lazy(() => import('suneditor-react'));

const CONTENT_TYPE_OPTIONS = [
  'Summary- Compliance',
  'Summary- Informative',
  'Summary- Policy/ System Change',
  'Surveillance & Other Operational Circulars',
  'Mock Trading',
  'Settlement Updates',
  'Listing & Suspension  Updates',
  'Commodity Updates',
  'Not Relevant Updates',
  'Consultation Paper',
  'Orders',
  'Others',
  'Approved Securities/ Banks',
];

const CIRCULAR_TYPE_OPTIONS = [
  'Circular No.',
  'Notice No.',
  'Order No.',
  'Others',
  'Press Release',
  "Communique' No.",
  'Notification No.',
];

const theme = createTheme({
  palette: {
    primary: { main: '#0060A6' },
    secondary: { main: '#F57C00' },
    success: { main: '#2E7D32' },
    error: { main: '#C62828' },
    warning: { main: '#F9A825' },
    background: { default: '#F1F5F9' },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: '"Inter","Roboto",sans-serif',
    button: { textTransform: 'none', fontWeight: 600 },
    h4: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'transform .2s ease',
          boxShadow: '0 2px 6px rgba(0,0,0,.06)',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 6px 16px rgba(0,0,0,.08)',
          },
        },
      },
    },
    MuiToggleButton: { styleOverrides: { root: { border: 0 } } },
  },
});

/* ---------------- helpers ---------------- */
const isToday = d =>
  new Date(d).toDateString() === new Date().toDateString();

const isWithinLastDays = (d, n) => {
  const diff = Date.now() - new Date(d).getTime();
  return diff <= n * 864e5 && diff >= 0;
};

const daysUntil = d =>
  Math.ceil((new Date(d) - Date.now()) / 864e5);
/*
const statusMeta = (issueDate, deadline) => {
  if (!deadline) return { label: 'No deadline', color: 'default' };
  const diff = daysUntil(deadline);
  if (diff < 0)
    return { label: `Overdue ${Math.abs(diff)}d`, color: 'error' };
  if (diff <= 3)
    return { label: `Due soon ${diff}d`, color: 'warning' };
  return { label: `${diff}d left`, color: 'success' };
};
*/
const uniq = arr => Array.from(new Set(arr));

const toYMDLocal = d => {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const CARD_MIN_H = { xs: 235, sm: 245, md: 255 };
const TITLE_MAX_LINES = 3;

const emptyRelRow = { exchange: '', linkOrUIN: '', circularDate: '' };

const PAGE_SIZES = [20, 50, 100, 200];

const emptyFilters = {
  contentType: '',
  dateFrom: '',
  dateTo: '',
  onDate: '',
  circularType: '',
  lawAreas: [],
  departments: [],
};

const emptyForm = {
  title: '',
  criticalityLevel: 'Low',
  lawArea: '',
  contentType: CONTENT_TYPE_OPTIONS[0],
  issueDate: '',
  complianceDeadlines: [],
  updateNumbers: [],
  tags: '',
  complianceURL: '',
  relatedURLs: [emptyRelRow],
  departments: [],
  summary: '',
  details: '',
  documents: [],
};

const TAB_KEYS = ['all', 'today', 'week', 'critical'];

export default function LegalUpdatesPage() {
  const [{ user, loading: authLoading }] = useAuth();
  const isAdmin = user?.role === 'admin' || user === 'admin';
  const isComplianceUser = Array.isArray(user?.departments)
    && user.departments.some(dep => String(dep || '').trim().toLowerCase() === 'compliance');
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState(1);

  const [updates, setUpdates] = useState([]);
  const [lawAreas, setLawAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState(0);
  const activeTabKey = TAB_KEYS[tab];

  // per-tab search terms
  const [queriesByTab, setQueriesByTab] = useState({
    all: '',
    today: '',
    week: '',
    critical: '',
  });
  const query = queriesByTab[activeTabKey];

  const [searchBusy, setSearchBusy] = useState(false);
  const [viewMode, setViewMode] = useState('tile');
  const [openCreate, setOpenCreate] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [sortNewestFirst, setSort] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [form, setForm] = useState(emptyForm);
  const [filters, setFilters] = useState(emptyFilters);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({
    all: 0,
    today: 0,
    week: 0,
    critical: 0,
  });

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;

    (async () => {
      try {
        const { data } = await api.get(
          `legal-update/legal-updates/${editId}`
        );
        setUpdates(prev => {
          const exists = prev.some(u => u._id === data._id);
          return exists
            ? prev.map(u => (u._id === data._id ? data : u))
            : [data, ...prev];
        });
        setForm(convertDocToForm(data));
        setEditing(editId);
        setOpenCreate(true);
        setSelected(null);
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
          'Could not load update to edit'
        );
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams]);

  const queryParams = useMemo(() => {
    const params = {
      contentType: filters.contentType || undefined,
      lawArea: filters.lawAreas?.length ? filters.lawAreas.join(',') : undefined,
      department: filters.departments?.length ? filters.departments.join(',') : undefined,
      sort: sortNewestFirst ? '-issueDate' : 'issueDate',
      page,
      limit: pageSize,
    };

    // ---------------- Issue Date filters ----------------
    if (filters.onDate) {
      params.onDate = filters.onDate;
      params.issueFrom = filters.onDate;
      params.issueTo = filters.onDate;
    }

    const todayStr = toYMDLocal(new Date());

    if (!filters.onDate) {
      if (activeTabKey === 'today') {
        params.issueFrom = todayStr;
        params.issueTo = todayStr;
      } else if (activeTabKey === 'week') {
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 7);
        const fromStr = toYMDLocal(from);
        const toStr = toYMDLocal(to);
        params.issueFrom = fromStr;
        params.issueTo = toStr;
      }
    }

    if (activeTabKey === 'critical') {
      params.critical = 'true';
    }

    // ---------------- Compliance Deadline range ----------------
    if (filters.dateFrom) params.deadlineFrom = filters.dateFrom;
    if (filters.dateTo) params.deadlineTo = filters.dateTo;
    if (filters.dateFrom || filters.dateTo) {
      // Ensure backend never interprets this range as issue-date fallback.
      params.dateField = 'deadline';
    }

    if (query.trim()) {
      params.q = query.trim();
    }

    return params;
  }, [
    filters.contentType,
    filters.lawAreas,
    filters.departments,
    filters.dateFrom,
    filters.dateTo,
    filters.onDate,
    sortNewestFirst,
    page,
    pageSize,
    activeTabKey,
    query,
  ]);

  /* ------ fetching list (single source of truth) ------ */
  const fetchList = useCallback(async () => {
    try {
      setSearchBusy(true);

      const { data } = await api.get(
        `legal-update/legal-updates`,
        { params: queryParams }
      );


      const items = Array.isArray(data?.data) ? data.data : [];
      setUpdates(items);

      const totalNum = Number(data?.total ?? 0);
      setTotal(totalNum);

      const pagesNum = Number(data?.pages ?? (Math.ceil(totalNum / pageSize) || 1));
      setPages(pagesNum);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load legal updates');
    } finally {
      setSearchBusy(false);
    }
  }, [queryParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchList();
    }, 200); // small debounce for filters/sort changes
    return () => clearTimeout(t);
  }, [fetchList]);

  // reset page when filters / sort / tab change
  // reset page when filters / sort / tab change
  // [CHANGE] depend on primitive filter values (not object identity) to avoid page snapping back to 1
  useEffect(() => {
    setPage(1);
  }, [
    filters.contentType,
    filters.circularType,
    filters.onDate,
    filters.dateFrom,
    filters.dateTo,
    (filters.lawAreas || []).join('|'),
    (filters.departments || []).join('|'),
    sortNewestFirst,
    activeTabKey,
  ]);


  const validDates = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map(d => (d ? new Date(d) : null))
      .filter(d => d instanceof Date && !Number.isNaN(d.getTime()));

  const effectiveDeadline = (deadlines) => {
    const ds = validDates(deadlines);
    if (!ds.length) return null;

    const now = Date.now();
    const upcoming = ds.filter(d => d.getTime() >= now).sort((a, b) => a - b);
    if (upcoming.length) return upcoming[0];

    return ds.sort((a, b) => b - a)[0];
  };

  const daysUntilFromDate = (dateObj) =>
    Math.ceil((dateObj.getTime() - Date.now()) / 864e5);

  const statusMetaFromDeadlines = (issueDate, deadlines) => {
    const d = effectiveDeadline(deadlines);
    if (!d) return { label: 'No deadline', color: 'default' };
    const diff = daysUntilFromDate(d);
    if (diff < 0) return { label: `Overdue ${Math.abs(diff)}d`, color: 'error' };
    if (diff <= 3) return { label: `Due soon ${diff}d`, color: 'warning' };
    return { label: `${diff}d left`, color: 'success' };
  };

  // ------ global counts for chips (All / Today / Week / Critical) ------
  useEffect(() => {
    const today = new Date();
    const todayStr = toYMDLocal(today);

    const weekTo = new Date();
    const weekFrom = new Date();
    weekFrom.setDate(weekFrom.getDate() - 7);
    const weekFromStr = toYMDLocal(weekFrom);
    const weekToStr = toYMDLocal(weekTo);

    (async () => {
      try {
        const [allRes, todayRes, weekRes, criticalRes] =
          await Promise.all([
            // All
            api.get(`legal-update/legal-updates`, {
              params: { sort: '-issueDate', limit: 1 },
            }),
            // Today
            api.get(`legal-update/legal-updates`, {
              params: {
                sort: '-issueDate',
                issueFrom: todayStr,
                issueTo: todayStr,
                limit: 1,
              },
            }),
            // This week
            api.get(`legal-update/legal-updates`, {
              params: {
                sort: '-issueDate',
                issueFrom: weekFromStr,
                issueTo: weekToStr,
                limit: 1,
              },
            }),
            // Critical (<=10 days)
            api.get(`legal-update/legal-updates`, {
              params: { sort: '-issueDate', critical: 'true', limit: 1 },
            }),
          ]);

        setCounts({
          all: allRes.data?.total ?? 0,
          today: todayRes.data?.total ?? 0,
          week: weekRes.data?.total ?? 0,
          critical: criticalRes.data?.total ?? 0,
        });
      } catch (err) {
        console.error('Failed to load counts:', err);
      }
    })();
  }, []);

  /* ------ law areas list ------ */
  const fetchLawAreas = async (setLawAreasFn) => {
    try {
      const response = await api.get('tracker/law-area');
      if (response.data?.lawareas) {
        setLawAreasFn(
          response.data.lawareas.map(x => x.law_area)
        );
      }
    } catch (error) {
      // 401 (session expired) is handled globally by the axios interceptor,
      // which opens the session-expired modal and redirects to login.
      console.error('Error fetching law areas:', error);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchLawAreas(setLawAreas);
      setLoading(false);
    })();
  }, []);

  /* ------ local filtering & grouping ------ */
  const filtered = useMemo(() => {
    let out = [...updates];

    if (filters.contentType)
      out = out.filter(
        u => u.contentType === filters.contentType
      );

    if (filters.dateFrom && filters.dateTo) {
      const f = new Date(filters.dateFrom);
      const t = new Date(filters.dateTo);
      out = out.filter(u => {
        const ed = effectiveDeadline(u.complianceDeadlines);
        if (!ed) return false;
        return ed >= f && ed <= t;
      });
    }

    if (filters.onDate) {
      const d = new Date(filters.onDate).toDateString();
      out = out.filter(
        u => new Date(u.issueDate).toDateString() === d
      );
    }

    if (filters.circularType) {
      const key = filters.circularType
        .split(' ')[0]
        .toLowerCase();
      out = out.filter(u => {
        const arr = Array.isArray(u.updateNumbers) ? u.updateNumbers : [];
        return arr.some(x => String(x || '').toLowerCase().includes(key));
      });
    }

    out.sort((a, b) =>
      sortNewestFirst
        ? new Date(b.issueDate) - new Date(a.issueDate)
        : new Date(a.issueDate) - new Date(b.issueDate)
    );

    return out;
  }, [updates, filters, sortNewestFirst]);

  const grouped = useMemo(
    () => ({
      all: filtered,
      today: filtered.filter(u => isToday(u.issueDate)),
      week: filtered.filter(u =>
        isWithinLastDays(u.issueDate, 7)
      ),
      critical: filtered.filter(u => {
        const ed = effectiveDeadline(u.complianceDeadlines);
        if (!ed) return false;
        const d = daysUntilFromDate(ed);
        return d >= 0 && d <= 10;
      }),
    }),
    [filtered]
  );

  const uniqueLawAreas = useMemo(
    () =>
      uniq(
        grouped.all
          .flatMap(update =>
            Array.isArray(update.lawAreas) ? update.lawAreas : []
          )
          .filter(Boolean)
      ),
    [grouped.all]
  );

  const highPct = useMemo(() => {
    if (!grouped.all.length) return 0;
    const high = grouped.all.filter(
      u => u.criticalityLevel === 'High'
    ).length;
    return Math.round((high / grouped.all.length) * 100);
  }, [grouped.all]);

  /* ------ single doc load via route ------ */
  useEffect(() => {
    if (!routeId) {
      setSelected(null);
      return;
    }
    const hit = updates.find(u => u._id === routeId);
    if (hit) {
      setSelected(hit);
      return;
    }

    (async () => {
      try {
        const { data } = await api.get(
          `legal-update/legal-updates/${routeId}`
        );
        setUpdates(prev => {
          const inside = prev.find(x => x._id === data._id);
          return inside ? prev : [data, ...prev];
        });
        setSelected(data);
      } catch {
        navigate('/legal-update', { replace: true });
      }
    })();
  }, [routeId, updates, navigate]);

  /* ------ helpers ------- */
  function convertDocToForm(doc) {
    return {
      title: doc.title || '',
      criticalityLevel: doc.criticalityLevel || 'Low',
      lawArea:
        Array.isArray(doc.lawAreas) && doc.lawAreas.length > 0
          ? doc.lawAreas[0]
          : '',
      contentType: doc.contentType || '',
      issueDate: doc.issueDate
        ? new Date(doc.issueDate).toISOString().slice(0, 10)
        : '',
      complianceDeadlines: Array.isArray(doc.complianceDeadlines)
        ? doc.complianceDeadlines
            .map(d => new Date(d).toISOString().slice(0, 10))
            .filter(Boolean)
        : [],
      updateNumbers: Array.isArray(doc.updateNumbers)
        ? doc.updateNumbers.map(x => String(x || '').trim()).filter(Boolean)
        : [],
      tags: Array.isArray(doc.tags)
        ? doc.tags.join(', ')
        : '',
      complianceURL: doc.complianceURL || '',
      relatedURLs:
        Array.isArray(doc.relatedURLs) &&
          doc.relatedURLs.length > 0
          ? doc.relatedURLs.map(r => ({
            exchange: r.exchange,
            linkOrUIN: r.linkOrUIN,
            circularDate: r.circularDate
              ? new Date(r.circularDate)
                .toISOString()
                .slice(0, 10)
              : '',
          }))
          : [{ exchange: '', linkOrUIN: '', circularDate: '' }],
      departments: Array.isArray(doc.departments)
        ? doc.departments.filter(Boolean)
        : [],
      summary: doc.summary || '',
      details: doc.details || '',
      documents: [],
    };
  }

  const handleChange = e => {
    const { name, value, files } = e.target;
    if (files)
      setForm(f => ({
        ...f,
        documents: Array.from(files),
      }));
    else setForm(f => ({ ...f, [name]: value }));
  };

  const updateRelRow = (i, k, v) =>
    setForm(f => {
      const rows = [...f.relatedURLs];
      rows[i] = { ...rows[i], [k]: v };
      return { ...f, relatedURLs: rows };
    });

  const addRelRow = () =>
    setForm(f => ({
      ...f,
      relatedURLs: [...f.relatedURLs, emptyRelRow],
    }));

  const removeRelRow = i =>
    setForm(f => ({
      ...f,
      relatedURLs: f.relatedURLs.filter(
        (_, idx) => idx !== i
      ),
    }));

  const handleSubmit = async e => {
    e.preventDefault();
    const splitCSV = (s) => String(s || '')
      .split(',')
      .map(x => x.trim())
      .filter(Boolean);

    const payload = {
      ...form,
      lawAreas: form.lawArea ? [form.lawArea] : [],
      departments: Array.isArray(form.departments) ? form.departments : [],
      updateNumbers: Array.isArray(form.updateNumbers) ? form.updateNumbers : [],
      complianceDeadlines: Array.isArray(form.complianceDeadlines)
        ? form.complianceDeadlines.map(d => new Date(d))
        : [],
      relatedURLs: form.relatedURLs.filter(r => r.exchange && r.linkOrUIN && r.circularDate),
    };
    try {
      let created;
      if (editing) {
        const { data } = await api.put(
          `legal-update/legal-updates/${editing}`,
          payload
        );
        created = data;
      } else {
        const { data } = await api.post(
          `legal-update/legal-updates`,
          payload
        );
        created = data;
      }
      setUpdates(u =>
        editing
          ? u.map(rec =>
            rec._id === created._id ? created : rec
          )
          : [created, ...u]
      );
      setOpenCreate(false);
      setForm(emptyForm);
      if (form.documents.length) {
        const uploads = form.documents.map(f => {
          const fd = new FormData();
          fd.append('updateId', created._id);
          fd.append('documents', f);
          return api.post(
            `s3/legal-updates/upload`,
            fd
          );
        });
        Promise.all(uploads).catch(err =>
          console.error('Doc upload err', err)
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error || 'Save failed'
      );
    }
  };

  // Clear filters + all search + reset page
  const resetFilters = () => {
    setFilters(emptyFilters);
    setQueriesByTab({
      all: '',
      today: '',
      week: '',
      critical: '',
    });
    setPage(1);
    setOpenFilter(false);
  };

  const exportVisibleCSV = rows => {
    try {
      const cols = [
        'Title',
        'Content Type',
        'Criticality',
        'Issue Date',
        'Compliance Deadline',
        'Law Areas',
        'Update Number',
        'Tags',
      ];
      const esc = s =>
        `"${String(s ?? '').replace(/"/g, '""')}"`;
      const lines = [cols.join(',')];
      rows.forEach(u => {
        lines.push(
          [
            u.title,
            u.contentType,
            u.criticalityLevel || '',
            u.issueDate
              ? new Date(u.issueDate)
                .toISOString()
                .slice(0, 10)
              : '',
            effectiveDeadline(u.complianceDeadlines)
              ? new Date(effectiveDeadline(u.complianceDeadlines))
                .toISOString()
                .slice(0, 10)
              : '',
            Array.isArray(u.lawAreas)
              ? u.lawAreas.join('; ')
              : '',
            Array.isArray(u.updateNumbers) ? u.updateNumbers.join('; ') : '',
            Array.isArray(u.tags)
              ? u.tags.join('; ')
              : u.tags || '',
          ]
            .map(esc)
            .join(',')
        );
      });
      const blob = new Blob([lines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legal-updates-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Could not export CSV');
    }
  };

  // tab + search handlers
  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    setPage(1);
  };

  const handleSearch = value => {

    // [CHANGE] if value didn’t change, do nothing (prevents pagination snap-back)
    if (queriesByTab[activeTabKey] === value) return;

    setQueriesByTab(prev => ({
      ...prev,
      [activeTabKey]: value,
    }));
    // [CHANGE] reset page only when search term actually changed
    setPage(1);
  };


  /* ------ derive active & paginated lists (server-side) ------ */
  const activeList = updates;

  const activeTotal = total;
  const pageCount = Math.max(
    1,
    Math.ceil(activeTotal / pageSize)
  );

  const paginatedList = activeList;

  const from =
    activeTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, activeTotal);

  if (loading || authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={50} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <Layout title="Legal Update">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container sx={{ pt: 3, pb: 5 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography
              variant="h4"
              color="primary.dark"
            >
              Legal Updates
            </Typography>
            <Box display="flex" alignItems="center">
              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                sx={{ mr: 1, bgcolor: '#fff' }}
              >
                <ToggleButton value="tile">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Toggle sort order">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SortIcon />}
                  sx={{ mr: 1 }}
                  onClick={() =>
                    setSort(p => !p)
                  }
                >
                  {sortNewestFirst
                    ? 'Newest'
                    : 'Oldest'}
                </Button>
              </Tooltip>
              <Tooltip title="Filter updates">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  sx={{ mr: 1 }}
                  onClick={() =>
                    setOpenFilter(true)
                  }
                >
                  Filter
                </Button>
              </Tooltip>
              <Tooltip title="Export visible as CSV">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() =>
                    exportVisibleCSV(activeList)
                  }
                >
                  Export
                </Button>
              </Tooltip>

              {/* Page size selector */}
              <FormControl
                size="small"
                sx={{ ml: 1, minWidth: 100 }}
              >
                <InputLabel id="page-size">
                  Rows
                </InputLabel>
                <Select
                  labelId="page-size"
                  label="Rows"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(
                      Number(e.target.value)
                    );
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map(s => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isAdmin && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setEditing(null);
                    setForm(emptyForm);
                    setOpenCreate(true);
                  }}
                  sx={{ ml: 1 }}
                >
                  + New Update
                </Button>
              )}
            </Box>
          </Box>

          {/* summary chips */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, bgcolor: '#fff' }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  icon={<LabelImportantIcon />}
                  label={`All: ${counts.all}`}
                />
                <Chip
                  icon={<TodayIcon />}
                  color="info"
                  label={`Today: ${counts.today}`}
                />
                <Chip
                  icon={<UpdateIcon />}
                  color="success"
                  label={`This Week: ${counts.week}`}
                />
                <Chip
                  color="warning"
                  label={`Critical (<=10d): ${counts.critical}`}
                />
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Chip
                  variant="outlined"
                  label={`Law areas: ${uniqueLawAreas.length}`}
                />
                <Chip
                  variant="outlined"
                  color="error"
                  label={`High: ${highPct}%`}
                />
              </Stack>
            </Stack>
          </Paper>

          {/* tabs */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
          >
            <Tab label={`All (${counts.all})`} />
            <Tab label={`Today (${counts.today})`} />
            <Tab label={`This Week (${counts.week})`} />
            <Tab
              label={`Critical (${counts.critical})`}
            />
          </Tabs>

          {/* search bar – key so it remounts per tab */}
          <Box
            display="flex"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <SearchBar
              key={activeTabKey}
              onSearch={handleSearch}
              busy={searchBusy}
            />
          </Box>

          {/* active filters chips */}
          {(query ||
            filters.contentType ||
            filters.lawAreas?.length ||
            filters.departments?.length ||
            filters.dateFrom ||
            filters.dateTo ||
            filters.onDate ||
            filters.circularType) && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ mr: 1 }}
                >
                  Active filters:
                </Typography>
                {query && (
                  <Chip
                    size="small"
                    label={`Search: ${query}`}
                    onDelete={() =>
                      setQueriesByTab(prev => ({
                        ...prev,
                        [activeTabKey]: '',
                      }))
                    }
                  />
                )}
                {filters.contentType && (
                  <Chip
                    size="small"
                    label={`Type: ${filters.contentType}`}
                    onDelete={() =>
                      setFilters(f => ({
                        ...f,
                        contentType: '',
                      }))
                    }
                  />
                )}
                {Array.isArray(
                  filters.lawAreas
                ) &&
                  filters.lawAreas.map(l => (
                    <Chip
                      key={l}
                      size="small"
                      label={`Law: ${l}`}
                      onDelete={() =>
                        setFilters(f => ({
                          ...f,
                          lawAreas:
                            f.lawAreas.filter(
                              x => x !== l
                            ),
                        }))
                      }
                    />
                  ))}
                {Array.isArray(filters.departments) &&
                  filters.departments.map(dep => (
                    <Chip
                      key={dep}
                      size="small"
                      label={`Dept: ${dep}`}
                      onDelete={() =>
                        setFilters(f => ({
                          ...f,
                          departments: f.departments.filter(x => x !== dep),
                        }))
                      }
                    />
                  ))}
                {filters.circularType && (
                  <Chip
                    size="small"
                    label={`Circular: ${filters.circularType}`}
                    onDelete={() =>
                      setFilters(f => ({
                        ...f,
                        circularType: '',
                      }))
                    }
                  />
                )}
                {(filters.dateFrom ||
                  filters.dateTo) && (
                    <Chip
                      size="small"
                      label={`Deadline: ${filters.dateFrom || '—'
                        } → ${filters.dateTo || '—'}`}
                      onDelete={() =>
                        setFilters(f => ({
                          ...f,
                          dateFrom: '',
                          dateTo: '',
                        }))
                      }
                    />
                  )}
                {filters.onDate && (
                  <Chip
                    size="small"
                    label={`Issue on: ${filters.onDate}`}
                    onDelete={() =>
                      setFilters(f => ({
                        ...f,
                        onDate: '',
                      }))
                    }
                  />
                )}
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={resetFilters}
                >
                  Clear all
                </Button>
              </Stack>
            )}

          {/* Pagination header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {activeTotal
                ? `Showing ${from}-${to} of ${activeTotal}`
                : '—'}
            </Typography>
            <Pagination
              color="primary"
              page={page}
              count={pages}
              onChange={(e, p) => {
                setPage(p);
              }}
              size="small"
            />
          </Box>

          {/* grid */}
          <Grid
            container
            spacing={viewMode === 'tile' ? 3 : 1}
            alignItems="stretch"
          >
            {paginatedList.map(u => {
              const meta = statusMetaFromDeadlines(u.issueDate, u.complianceDeadlines);
              const nums = Array.isArray(u.updateNumbers) ? u.updateNumbers.filter(Boolean) : [];
              const primaryNum = nums[0] || '';
              const d = effectiveDeadline(u.complianceDeadlines)
                ? daysUntilFromDate(effectiveDeadline(u.complianceDeadlines))
                : null;
              const bar =
                d == null
                  ? theme.palette.grey[500]
                  : d < 0
                    ? theme.palette.error.main
                    : d <= 3
                      ? theme.palette.warning.main
                      : theme.palette.success.main;
              const extra = Math.max(0, nums.length - 1);

              return (
                <Grid
                  item
                  xs={12}
                  sm={
                    viewMode === 'tile' ? 6 : 12
                  }
                  md={
                    viewMode === 'tile' ? 4 : 12
                  }
                  key={u._id}
                  sx={{ display: 'flex' }}
                >
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      ...(viewMode === 'tile'
                        ? {
                          minHeight:
                            CARD_MIN_H,
                        }
                        : {}),
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        bgcolor: bar,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                      }}
                    />
                    <CardActionArea
                      sx={{
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        width: '100%',
                      }}
                      component={Link}
                      to={`/legal-update/${u._id}`}
                      state={{ background: location }}
                    >
                      <CardContent
                        sx={{
                          p: 0,
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{
                              width: '100%',
                              display: '-webkit-box',
                              WebkitLineClamp: TITLE_MAX_LINES,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {u.title}
                          </Typography>

                          {primaryNum && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={primaryNum}
                                sx={{
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    maxWidth: 220,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  },
                                }}
                              />
                              {extra > 0 && <Chip size="small" variant="outlined" label={`+${extra}`} />}
                            </Box>
                          )}
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={1}
                          noWrap
                          sx={{
                            overflow: 'hidden',
                            textOverflow:
                              'ellipsis',
                          }}
                        >
                          {u.lawAreas?.join(
                            ', '
                          ) || '—'}{' '}
                          • {u.contentType}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 'auto' }}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          {u.criticalityLevel && (
                            <Chip
                              label={
                                u.criticalityLevel
                              }
                              size="small"
                              color={
                                u.criticalityLevel ===
                                  'High'
                                  ? 'error'
                                  : u.criticalityLevel ===
                                    'Medium'
                                    ? 'warning'
                                    : 'default'
                              }
                            />
                          )}
                          <Chip
                            size="small"
                            variant="outlined"
                            color={meta.color}
                            label={meta.label}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 'auto' }}
                          >
                            {new Date(
                              u.issueDate
                            ).toLocaleDateString()}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
            {paginatedList.length === 0 && (
              <Grid item xs={12}>
                <Box
                  textAlign="center"
                  sx={{ py: 10, opacity: 0.6 }}
                >
                  <Typography variant="subtitle1">
                    No updates match these
                    criteria.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* bottom pagination */}
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Pagination
              color="primary"
              page={page}
              count={pages}
              onChange={(e, p) => {
                setPage(p);
              }}
              size="small"
            />
          </Box>

          {/* detail dialog */}
          <LegalUpdateDetail
            open={Boolean(selected)}
            onClose={() => setSelected(null)}
            update={selected}
            role={user.role}
            onEdit={u => {
              setOpenCreate(true);
              setForm(convertDocToForm(u));
              setEditing(u._id);
            }}
            onRefresh={async id => {
              if (!id) return;
              const { data } = await api.get(
                `legal-update/legal-updates/${id}`
              );
              setUpdates(prev =>
                prev.map(d =>
                  d._id === data._id ? data : d
                )
              );
              setSelected({ ...data });
            }}
          />

          {/* create/edit dialog */}
          {isAdmin && (
            <Dialog
              open={openCreate}
              onClose={() => setOpenCreate(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle fontWeight={700}>
                {editing
                  ? 'Edit Legal Update'
                  : 'Create Legal Update'}
              </DialogTitle>
              <DialogContent
                dividers
                sx={{ background: '#FAFCFF' }}
              >
                <Paper elevation={0} sx={{ p: 2 }}>
                  <Stack spacing={3}>
                    <TextField
                      label="Title"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      required
                      fullWidth
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>
                            Criticality
                          </InputLabel>
                          <Select
                            name="criticalityLevel"
                            label="Criticality"
                            value={
                              form.criticalityLevel
                            }
                            onChange={handleChange}
                          >
                            {['Low', 'Medium', 'High'].map(
                              l => (
                                <MenuItem
                                  key={l}
                                  value={l}
                                >
                                  {l}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>
                            Law Area
                          </InputLabel>
                          <Select
                            name="lawArea"
                            label="Law Area"
                            value={form.lawArea}
                            onChange={handleChange}
                          >
                            {lawAreas.map(l => (
                              <MenuItem
                                key={l}
                                value={l}
                              >
                                {l}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Departments</InputLabel>
                          <Select
                            multiple
                            name="departments"
                            label="Departments"
                            value={form.departments || []}
                            onChange={handleChange}
                            renderValue={(selected) => selected.join(', ')}
                          >
                            {LEGAL_UPDATE_DEPARTMENT_OPTIONS.map(dep => (
                              <MenuItem key={dep} value={dep}>
                                <Checkbox checked={(form.departments || []).includes(dep)} />
                                <ListItemText primary={dep} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={7}>
                        <FormControl fullWidth>
                          <InputLabel>
                            Content Type
                          </InputLabel>
                          <Select
                            name="contentType"
                            label="Content Type"
                            value={form.contentType}
                            onChange={handleChange}
                          >
                            {CONTENT_TYPE_OPTIONS.map(
                              ct => (
                                <MenuItem
                                  key={ct}
                                  value={ct}
                                >
                                  {ct}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <MultiStringInput
                          label="Update Numbers"
                          value={form.updateNumbers}
                          onChange={(arr) => setForm(f => ({ ...f, updateNumbers: arr }))}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          type="date"
                          name="issueDate"
                          label="Issue Date"
                          required
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                          value={form.issueDate}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MultiDatePicker
                          label="Compliance Deadlines"
                          value={form.complianceDeadlines}
                          onChange={(arr) => setForm(f => ({ ...f, complianceDeadlines: arr }))}
                        />
                      </Grid>
                    </Grid>
                    <TextField
                      label="Tags (comma separated)"
                      name="tags"
                      value={form.tags}
                      onChange={handleChange}
                      fullWidth
                    />
                    <TextField
                      label="Compliance URL"
                      name="complianceURL"
                      value={form.complianceURL}
                      onChange={handleChange}
                      fullWidth
                    />
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography fontWeight={600}>
                          Related URLs
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={addRelRow}
                        >
                          Add
                        </Button>
                      </Stack>
                      {form.relatedURLs.map(
                        (row, i) => (
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            key={i}
                            sx={{ mb: 1 }}
                          >
                            <Grid
                              item
                              xs={12}
                              md={3}
                            >
                              <TextField
                                label="Exchange"
                                size="small"
                                value={row.exchange}
                                onChange={e =>
                                  updateRelRow(
                                    i,
                                    'exchange',
                                    e.target.value
                                  )
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={5}
                            >
                              <TextField
                                label="Link / UIN"
                                size="small"
                                value={row.linkOrUIN}
                                onChange={e =>
                                  updateRelRow(
                                    i,
                                    'linkOrUIN',
                                    e.target.value
                                  )
                                }
                                fullWidth
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={3}
                            >
                              <TextField
                                type="date"
                                label="Circular Date"
                                size="small"
                                fullWidth
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                value={
                                  row.circularDate
                                }
                                onChange={e =>
                                  updateRelRow(
                                    i,
                                    'circularDate',
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={1}
                              textAlign="center"
                            >
                              {form
                                .relatedURLs
                                .length > 1 && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      removeRelRow(
                                        i
                                      )
                                    }
                                  >
                                    <DeleteForeverIcon
                                      fontSize="small"
                                    />
                                  </IconButton>
                                )}
                            </Grid>
                          </Grid>
                        )
                      )}
                    </Box>
                    <TextField
                      label="Summary"
                      name="summary"
                      value={form.summary}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      fullWidth
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1 }}
                      >
                        Details (rich-text)
                      </Typography>
                      <Suspense
                        fallback={
                          <Box
                            sx={{
                              height: 180,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent:
                                'center',
                            }}
                          >
                            <CircularProgress
                              size={20}
                            />
                          </Box>
                        }
                      >
                        <SunEditor
                          theme="snow"
                          setContents={form.details}
                          onChange={v =>
                            setForm(f => ({
                              ...f,
                              details: v,
                            }))
                          }
                          style={{ height: 180 }}
                        />
                      </Suspense>
                    </Box>
                    <Divider />
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={2}
                      alignItems="center"
                    >
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<BackupIcon />}
                      >
                        Upload documents
                        <input
                          hidden
                          multiple
                          type="file"
                          name="documents"
                          onChange={handleChange}
                        />
                      </Button>
                      <Typography variant="caption">
                        {form.documents.length
                          ? `${form.documents.length} file(s) selected`
                          : 'PDF, Word, Excel…'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setOpenCreate(false);
                    setEditing(null);
                    setForm(emptyForm);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="secondary"
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          )}

          {/* filter dialog */}
          <Dialog
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle fontWeight={700}>
              Filter Updates
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>
                    Content Type
                  </InputLabel>
                  <Select
                    value={filters.contentType}
                    label="Content Type"
                    onChange={e =>
                      setFilters(f => ({
                        ...f,
                        contentType:
                          e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {CONTENT_TYPE_OPTIONS.map(o => (
                      <MenuItem
                        key={o}
                        value={o}
                      >
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Law Area – multi-select filter */}
                <FormControl fullWidth>
                  <InputLabel>Law Area</InputLabel>
                  <Select
                    multiple
                    value={filters.lawAreas}
                    label="Law Area"
                    onChange={e =>
                      setFilters(f => ({
                        ...f,
                        lawAreas:
                          e.target.value,
                      }))
                    }
                    renderValue={selected =>
                      selected.join(', ')
                    }
                  >
                    {lawAreas.map(l => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {isComplianceUser && (
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      multiple
                      value={filters.departments}
                      label="Department"
                      onChange={e =>
                        setFilters(f => ({
                          ...f,
                          departments: e.target.value,
                        }))
                      }
                      renderValue={selected => selected.join(', ')}
                    >
                      {LEGAL_UPDATE_DEPARTMENT_OPTIONS.map(dep => (
                        <MenuItem key={dep} value={dep}>
                          {dep}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel>
                    Circular No. Type
                  </InputLabel>
                  <Select
                    value={filters.circularType}
                    label="Circular No. Type"
                    onChange={e =>
                      setFilters(f => ({
                        ...f,
                        circularType:
                          e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {CIRCULAR_TYPE_OPTIONS.map(o => (
                      <MenuItem
                        key={o}
                        value={o}
                      >
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="subtitle2">
                  Start / End (Compliance
                  Deadline)
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                >
                  <TextField
                    type="date"
                    label="From"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={filters.dateFrom}
                    onChange={e =>
                      setFilters(f => ({
                        ...f,
                        dateFrom:
                          e.target.value,
                      }))
                    }
                  />
                  <TextField
                    type="date"
                    label="To"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={filters.dateTo}
                    onChange={e =>
                      setFilters(f => ({
                        ...f,
                        dateTo:
                          e.target.value,
                      }))
                    }
                  />
                </Stack>
                <TextField
                  type="date"
                  label="On Date (Issue Date)"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={filters.onDate}
                  onChange={e => {
                    setFilters(f => ({ ...f, onDate: e.target.value }));
                  }}

                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<ClearIcon />}
                onClick={resetFilters}
              >
                Reset
              </Button>
              <Button
                onClick={() => setOpenFilter(false)}
                variant="contained"
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>
          </LocalizationProvider>
        </Container>
      </ThemeProvider>
    </Layout>
  );
}
